import { all, takeEvery, put, call } from 'redux-saga/effects';
import ApiInstance from '../../helpers/apiInstance';
import { sendRequest } from '../../helpers/saga';
import { logger } from '../../helpers/logger';
import notifications from '../notifications/actions';
import actions from './actions';
import { ILegalEntitiesOptionsApiResponse, ILegalEntityOption } from './types';

const api = new ApiInstance('legal_entities/list');

// Larger page sizes are silently clamped to this ceiling.
const PAGE_SIZE = 100;

// Runaway guard, not an expected limit — 10,000 entities at this page size.
const MAX_PAGES = 100;

// A failed request resolves instead of rejecting, so the status is the only signal that one failed;
// without this check a 500 would read as an organisation with no legal entities.
const succeeded = (result: ILegalEntitiesOptionsApiResponse | undefined): boolean =>
  typeof result?.status === 'number' && result.status >= 200 && result.status < 300;

export function* fetchOptions(): Generator<unknown, void, ILegalEntitiesOptionsApiResponse> {
  // An uncaught throw here would tear the worker out through the root `all([...])` and take the
  // watcher with it, so every later fetch request would be ignored in silence.
  try {
    const collected: ILegalEntityOption[] = [];
    let page: number | null = 1;
    let pagesRead = 0;

    while (page !== null && pagesRead < MAX_PAGES) {
      const result: ILegalEntitiesOptionsApiResponse = yield call(
        sendRequest,
        api.get,
        { search: { page, page_size: PAGE_SIZE } },
        notifications.REQUEST_ERROR,
      );

      // A mid-walk failure is indistinguishable from the last page, so bail rather than return a
      // truncated list.
      if (!succeeded(result)) {
        logger.error(`[legalEntitiesOptions] page ${page} failed with status ${result?.status ?? 'unknown'}`);
        yield put(actions.fetchOptionsError());

        return;
      }

      collected.push(...(result.data?.records ?? []));

      const nextPage = result.data?.pagination?.next_page ?? null;

      // A non-advancing next_page would otherwise re-collect the same records until MAX_PAGES. Note this
      // reports SUCCESS with what was collected, so a stalled paginator yields a silently truncated list.
      page = nextPage !== null && nextPage > page ? nextPage : null;
      pagesRead += 1;
    }

    // The cursor, not the page count, says whether the walk finished: a list ending exactly on the
    // last allowed page is complete, and `pagesRead >= MAX_PAGES` would call it truncated.
    if (page !== null) {
      logger.error(`[legalEntitiesOptions] stopped after ${MAX_PAGES} pages holding ${collected.length} records`);
      yield put(actions.fetchOptionsError());

      return;
    }

    yield put(actions.fetchOptionsSuccess(collected));
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchOptionsError());
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.LEGAL_ENTITIES_OPTIONS_FETCH_REQUEST, fetchOptions)]);
}
